import { useControlledState, useEventCallback } from '@ixigo/iui/utils';
import { useEffect, useMemo, useRef } from 'react';
import cn from '../../utils/cn';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';
import type { DateTimePickerProps } from './types';
import {
	clampDateTime,
	computeTimeBounds,
	formatHHmm,
	parseDateInput,
	startOfDayIso,
} from './utils';

// Round minutes up to the nearest step (ceil). Seconds and ms set to 0.
function roundUpToStep(date: Date, step: number): Date {
	const d = new Date(date);
	const minutes = d.getMinutes();
	const remainder = minutes % step;
	if (remainder === 0) {
		d.setSeconds(0, 0);
		return d;
	}
	let nextMinutes = minutes + (step - remainder); // ceil
	if (nextMinutes >= 60) {
		d.setHours(d.getHours() + 1);
		nextMinutes -= 60;
	}
	d.setMinutes(nextMinutes, 0, 0);
	return d;
}

// Returns the clamped Date for an input value/default against given bounds
function getBoundedInputDate(
	value: NonNullable<DateTimePickerProps['value']>,
	min?: DateTimePickerProps['min'],
	max?: DateTimePickerProps['max'],
	step: number = 1,
) {
	const raw = parseDateInput(value);
	return roundUpToStep(clampDateTime(raw, min, max), step);
}

export function DateTimePicker(props: DateTimePickerProps) {
	const {
		value,
		defaultValue,
		visibleRows = 9,
		min: minProp,
		max: maxProp,
		className,
		classes,
		step = 1,
		selectionMode = 'segmented',
	} = props;
	// Helpers now from utils

	const defaultValueRef = useRef(defaultValue || new Date());
	const stableValueProp = useMemo(() => {
		return value
			? getBoundedInputDate(value, minProp, maxProp, step)
			: undefined;
	}, [value, minProp, maxProp, step]);

	const [selectedDate, setDate] = useControlledState<Date>({
		controlled: stableValueProp,
		default: getBoundedInputDate(
			defaultValueRef.current,
			minProp,
			maxProp,
			step,
		),
	});
	const dateIso = startOfDayIso(selectedDate);
	const timeHHmm = formatHHmm(selectedDate);

	const { timeMin, timeMax } = computeTimeBounds(
		selectedDate,
		minProp,
		maxProp,
	);

	// On mount only, emit the current clamped date so parents normalize state
	const didInitRef = useRef(false);
	const initialOutofBoundUpdateCallback = useEventCallback(
		(
			minProp: DateTimePickerProps['min'],
			maxProp: DateTimePickerProps['max'],
		) => {
			if (didInitRef.current) return;
			didInitRef.current = true;
			// Determine if provided value/default was out of bounds
			const providedRaw = parseDateInput(value ?? defaultValueRef.current);
			const bounded = getBoundedInputDate(providedRaw, minProp, maxProp, step);
			const wasOutOfBounds = providedRaw.getTime() !== bounded?.getTime();

			if (!wasOutOfBounds) return;
			setDate(bounded);
			props.onChange?.(bounded);
		},
	);

	useEffect(() => {
		initialOutofBoundUpdateCallback(minProp, maxProp);
	}, [initialOutofBoundUpdateCallback, minProp, maxProp]);

	const handleDateChange = (nextDate: Date) => {
		const next = new Date(nextDate);
		next.setHours(selectedDate.getHours(), selectedDate.getMinutes(), 0, 0);
		const bounded = clampDateTime(next, minProp, maxProp);
		setDate(bounded);
		props.onChange?.(bounded);
	};

	const handleTimeChange = (nextTime: string) => {
		const [h, m] = nextTime.split(':').map(Number);
		const next = new Date(selectedDate);
		next.setHours(h || 0, m || 0, 0, 0);
		const bounded = clampDateTime(next, minProp, maxProp);
		setDate(bounded);
		props.onChange?.(bounded);
	};

	return (
		<div className={cn('flex flex-row items-center gap-20', className)}>
			{
				<DatePicker
					value={dateIso}
					selectionMode={selectionMode}
					visibleRows={visibleRows}
					min={minProp}
					max={maxProp}
					onChange={handleDateChange}
					classes={classes?.date}
				/>
			}
			{
				<TimePicker
					value={timeHHmm}
					defaultValue={undefined}
					step={step}
					visibleRows={visibleRows}
					min={timeMin}
					max={timeMax}
					onChange={(time) => handleTimeChange(time)}
					classes={classes?.time}
				/>
			}
		</div>
	);
}
